/**
 * AnimationPreviewScene — Dev tool for previewing and editing spritesheet animations.
 *
 * Features:
 *  • Browse all loaded spritesheets
 *  • Editable frameWidth / frameHeight per spritesheet (re-slices texture live)
 *  • Frame timeline at the bottom with clickable thumbnails
 *  • Adjust per-frame duration, offset (x/y)
 *  • Play / pause / step controls
 *  • Save animation definitions to localStorage (auto-save)
 *  • BootScene reads saved frame configs at load time
 *  • Back button → TitleScene
 *
 * Accessible from TitleScene dev toolbar ("Animations" button).
 *
 * Decomposed into sub-modules:
 *  - animpreview/SourcePanel.ts   — Source spritesheet view, bbox overlay, pixel analysis
 *  - animpreview/PreviewPanel.ts  — Result preview, onion skin, bake & save
 *  - animpreview/AnimToolbar.ts   — Controls, timeline, sidebar, property panels
 *  - animpreview/layoutConstants.ts — Shared layout geometry
 *  - animpreview/AnimPreviewState.ts — Shared mutable state interface
 */

import Phaser from 'phaser';
import { CANVAS_WIDTH } from '@/config/GameConfig';
import { createTechButton, TECH } from '@/ui/TechUI';
import { SourcePanel } from './animpreview/SourcePanel';
import { PreviewPanel } from './animpreview/PreviewPanel';
import { AnimToolbar } from './animpreview/AnimToolbar';
import type { AnimPreviewState } from './animpreview/AnimPreviewState';
import spritesheetManifest from 'virtual:spritesheet-manifest';

const STORAGE_KEY = 'rfd_anim_defs';

// ── Persisted animation definition ──
export interface AnimFrameDef {
  duration: number;   // ms for this frame
  offsetX: number;    // px offset (legacy, derived from bbox)
  offsetY: number;
  scale: number;      // per-frame scale multiplier (legacy, derived from bbox)
  /** Bounding box in source frame coords: region to crop and fit into the output frame */
  bboxX?: number;     // left edge in frame-local px (default 0)
  bboxY?: number;     // top edge in frame-local px (default 0)
  bboxW?: number;     // width in px (default = frameWidth)
  bboxH?: number;     // height in px (default = frameHeight)
}

export interface AnimDef {
  key: string;            // animation name
  sheetKey: string;       // spritesheet texture key
  sourceFile: string;     // original filename in public/assets/ (for baking)
  frameWidth: number;     // px width of each frame
  frameHeight: number;    // px height of each frame
  frames: AnimFrameDef[]; // per-frame overrides (indexed by playback order)
  frameOrder: number[];   // playback order → spritesheet frame index
  repeat: number;         // -1 = loop, 0 = once
}

/** Load saved animation defs from localStorage */
export function loadAnimDefs(): Record<string, AnimDef> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) { console.warn('[AnimationPreview] loadAnimDefs failed:', e); return {}; }
}

/** Save animation defs to localStorage */
function saveAnimDefs(defs: Record<string, AnimDef>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(defs));
}

/**
 * Register all saved AnimDefs as Phaser animations.
 * Call once after textures are loaded (e.g. in BootScene.create()).
 * Uses per-frame duration and custom frameOrder from the editor.
 * Skips defs whose spritesheet texture isn't loaded.
 */
export function registerSavedAnimations(scene: Phaser.Scene): void {
  const defs = loadAnimDefs();
  for (const def of Object.values(defs)) {
    if (!def.frameOrder || def.frameOrder.length === 0) continue;
    if (!scene.textures.exists(def.sheetKey)) continue;
    // Remove existing anim with same key to allow overrides
    if (scene.anims.exists(def.key)) {
      scene.anims.remove(def.key);
    }
    const frames: Phaser.Types.Animations.AnimationFrame[] = def.frameOrder.map((sheetIdx, i) => ({
      key: def.sheetKey,
      frame: sheetIdx,
      duration: def.frames[i]?.duration ?? 100,
    }));
    scene.anims.create({
      key: def.key,
      frames,
      repeat: def.repeat,
    });
  }
}

export class AnimationPreviewScene extends Phaser.Scene {
  // ── Shared state (passed to sub-modules by reference) ──
  private state: AnimPreviewState = {
    sheetKeys: [],
    selectedSheet: '',
    frameCount: 0,
    selectedFrame: -1,
    animDef: null!,
    allDefs: {},
    playing: false,
    playTimer: 0,
    playFrameIdx: 0,
    onionEnabled: false,
    crosshairEnabled: false,
    showArchived: false,
    sidebarFilter: '',
  };

  // ── Delete confirmation (keyboard) ──
  private kbDeletePending = false;
  private kbDeleteTimeout: ReturnType<typeof setTimeout> | null = null;

  // ── Sub-modules ──
  private sourcePanel!: SourcePanel;
  private previewPanel!: PreviewPanel;
  private toolbar!: AnimToolbar;

  constructor() {
    super('AnimationPreviewScene');
  }

  create(): void {
    this.state.allDefs = loadAnimDefs();
    this.cameras.main.setBackgroundColor('#0d0d1a');

    // Discover all spritesheets
    this.state.sheetKeys = [];
    this.textures.each((tex: Phaser.Textures.Texture) => {
      if (tex.frameTotal > 2) {
        this.state.sheetKeys.push(tex.key);
      }
    }, this);
    this.state.sheetKeys.sort();

    // Require 10px movement before a drag starts (prevents click = swap)
    this.input.dragDistanceThreshold = 10;

    // ── Initialize sub-modules ──
    this.sourcePanel = new SourcePanel(this, this.state, {
      autoSave: () => this.autoSave(),
      refreshPreviewSprite: (idx) => this.previewPanel.refreshPreviewSprite(idx),
      refreshFrameProps: () => this.toolbar.refreshFrameProps(),
      refreshSheetView: () => this.sourcePanel.refreshSheetView(),
    });

    this.previewPanel = new PreviewPanel(this, this.state, {
      autoSave: () => this.autoSave(),
      refreshTimeline: () => this.toolbar.refreshTimeline(),
      refreshFrameProps: () => this.toolbar.refreshFrameProps(),
      refreshSheetProps: () => this.toolbar.refreshSheetProps(),
      rebuildSidebarList: () => this.toolbar.rebuildSidebarList(),
    }, this.sourcePanel);

    this.toolbar = new AnimToolbar(this, this.state, {
      autoSave: () => this.autoSave(),
      selectSheet: (key) => this.selectSheet(key),
      startPlayback: () => this.startPlayback(),
      stopPlayback: () => this.stopPlayback(),
      showFrame: (idx) => this.showFrame(idx),
      reSliceTexture: () => this.reSliceTexture(),
    }, this.previewPanel, this.sourcePanel);

    // ── Build UI ──
    this.toolbar.buildSidebar();
    this.sourcePanel.buildPanel();
    this.previewPanel.buildPanel();
    this.toolbar.buildTimeline();
    this.toolbar.buildControls();
    this.toolbar.buildFrameProps();
    this.toolbar.buildSheetProps();

    if (this.state.sheetKeys.length > 0) {
      this.selectSheet(this.state.sheetKeys[0]);
    }

    // Back button (top-right)
    this.add.existing(createTechButton(this, CANVAS_WIDTH - 50, 20, 'BACK', {
      onClick: () => this.previewPanel.tryExit(),
      width: 70, height: 28, fontSize: '11px', color: TECH.DANGER,
    }));

    // ESC → exit (with bake prompt if needed)
    this.input.keyboard!.on('keydown-ESC', () => this.previewPanel.tryExit());

    // Keyboard shortcuts — delete requires double-press within 1.5s
    const handleDeleteKey = () => {
      if (this.state.selectedFrame < 0) return;
      if (this.kbDeletePending) {
        if (this.kbDeleteTimeout) clearTimeout(this.kbDeleteTimeout);
        this.kbDeletePending = false;
        this.toolbar.deleteFrame(this.state.selectedFrame);
      } else {
        this.kbDeletePending = true;
        this.kbDeleteTimeout = setTimeout(() => { this.kbDeletePending = false; }, 1500);
      }
    };
    this.input.keyboard!.on('keydown-DELETE', handleDeleteKey);
    this.input.keyboard!.on('keydown-BACKSPACE', handleDeleteKey);
    this.input.keyboard!.on('keydown-D', (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        if (this.state.selectedFrame >= 0) this.toolbar.duplicateFrame(this.state.selectedFrame);
      }
    });

    // Arrow keys — frame navigation, dimensions, offset, scale
    const arrowHandler = (e: KeyboardEvent) => {
      const key = e.key;
      const st = this.state;
      if (!st.animDef) return;

      if (e.metaKey || e.ctrlKey) {
        if (st.selectedFrame < 0) return;
        const def = st.animDef.frames[st.selectedFrame];
        if (!def) return;
        const hasBbox = def.bboxW != null && def.bboxW > 0;
        if (hasBbox) {
          if (key === 'ArrowUp') def.bboxH = (def.bboxH ?? 0) + 1;
          else if (key === 'ArrowDown') def.bboxH = Math.max(8, (def.bboxH ?? 0) - 1);
          else if (key === 'ArrowLeft') def.bboxW = Math.max(8, def.bboxW! - 1);
          else if (key === 'ArrowRight') def.bboxW = def.bboxW! + 1;
          else return;
        } else {
          if (def.scale === undefined) def.scale = 1;
          if (key === 'ArrowUp') def.scale = Math.round(Math.min(def.scale + 0.05, 3) * 100) / 100;
          else if (key === 'ArrowDown') def.scale = Math.round(Math.max(def.scale - 0.05, 0.1) * 100) / 100;
          else return;
        }
        e.preventDefault();
        this.autoSave();
        this.showFrame(st.selectedFrame);
        this.toolbar.refreshFrameProps();
      } else if (e.shiftKey) {
        if (st.selectedFrame < 0) return;
        const def = st.animDef.frames[st.selectedFrame];
        if (!def) return;
        const hasBbox = def.bboxW != null && def.bboxW > 0;
        if (hasBbox) {
          if (key === 'ArrowLeft') def.bboxX = (def.bboxX ?? 0) + 1;
          else if (key === 'ArrowRight') def.bboxX = (def.bboxX ?? 0) - 1;
          else if (key === 'ArrowUp') def.bboxY = (def.bboxY ?? 0) + 1;
          else if (key === 'ArrowDown') def.bboxY = (def.bboxY ?? 0) - 1;
          else return;
        } else {
          if (key === 'ArrowLeft') def.offsetX--;
          else if (key === 'ArrowRight') def.offsetX++;
          else if (key === 'ArrowUp') def.offsetY--;
          else if (key === 'ArrowDown') def.offsetY++;
          else return;
        }
        e.preventDefault();
        this.autoSave();
        this.showFrame(st.selectedFrame);
        this.toolbar.refreshFrameProps();
      } else if (key === 'ArrowUp' || key === 'ArrowDown') {
        const delta = key === 'ArrowUp' ? 1 : -1;
        st.animDef.frameWidth = Math.max(8, st.animDef.frameWidth + delta);
        st.animDef.frameHeight = Math.max(8, st.animDef.frameHeight + delta);
        e.preventDefault();
        this.reSliceTexture();
        this.toolbar.refreshSheetProps();
      } else if (key === 'ArrowLeft' || key === 'ArrowRight') {
        if (st.frameCount === 0) return;
        if (key === 'ArrowLeft') {
          st.selectedFrame = Math.max(0, (st.selectedFrame < 0 ? 0 : st.selectedFrame) - 1);
        } else {
          st.selectedFrame = Math.min(st.frameCount - 1, (st.selectedFrame < 0 ? -1 : st.selectedFrame) + 1);
        }
        this.stopPlayback();
        this.showFrame(st.selectedFrame);
        this.toolbar.refreshTimeline();
        this.toolbar.refreshFrameProps();
        this.toolbar.scrollTimelineToFrame(st.selectedFrame);
      }
    };
    this.input.keyboard!.on('keydown', arrowHandler);

    // Spacebar → play/pause
    this.input.keyboard!.on('keydown-SPACE', (e: KeyboardEvent) => {
      e.preventDefault();
      if (this.state.playing) this.stopPlayback(); else this.startPlayback();
      this.toolbar.refreshControls();
    });
  }

  // ═══════════════════════════════════════════════════════
  // ── Sheet selection ──
  // ═══════════════════════════════════════════════════════

  private selectSheet(key: string): void {
    const st = this.state;
    this.stopPlayback();
    st.selectedSheet = key;
    st.selectedFrame = -1;

    const tex = this.textures.get(key);
    st.frameCount = tex ? tex.frameTotal - 1 : 0;

    const frame0 = tex?.get(0);
    const currentFw = frame0?.width ?? 64;
    const currentFh = frame0?.height ?? 64;

    if (st.allDefs[key]) {
      st.animDef = st.allDefs[key];
      if (st.animDef.frameWidth !== currentFw || st.animDef.frameHeight !== currentFh) {
        this.reSliceTexture();
        return;
      }
      while (st.animDef.frames.length < st.frameCount) {
        st.animDef.frames.push({ duration: 100, offsetX: 0, offsetY: 0, scale: 1 });
      }
      st.animDef.frames.length = st.frameCount;
      for (const f of st.animDef.frames) {
        if (f.scale === undefined) f.scale = 1;
      }
      if (!st.animDef.frameOrder || st.animDef.frameOrder.length !== st.frameCount) {
        st.animDef.frameOrder = Array.from({ length: st.frameCount }, (_, i) => i);
      }
      if (!st.animDef.sourceFile) {
        st.animDef.sourceFile = this.getSourceFilename(key);
      }
    } else {
      st.animDef = {
        key: key + '_anim',
        sheetKey: key,
        sourceFile: this.getSourceFilename(key),
        frameWidth: currentFw,
        frameHeight: currentFh,
        frames: Array.from({ length: st.frameCount }, () => ({ duration: 100, offsetX: 0, offsetY: 0, scale: 1 })),
        frameOrder: Array.from({ length: st.frameCount }, (_, i) => i),
        repeat: -1,
      };
      st.allDefs[key] = st.animDef;
    }

    this.previewPanel.sheetInfoLabel.setText(`${key}`);
    this.previewPanel.frameCountLabel.setText(`${st.frameCount} frames  •  ${currentFw}×${currentFh}px`);

    this.showFrame(0);
    this.toolbar.rebuildSidebarList();
    this.toolbar.refreshTimeline();
    this.toolbar.refreshFrameProps();
    this.toolbar.refreshSheetProps();
    this.toolbar.refreshControls();
  }

  // ═══════════════════════════════════════════════════════
  // ── Re-slice texture ──
  // ═══════════════════════════════════════════════════════

  private reSliceTexture(): void {
    const st = this.state;
    const key = st.selectedSheet;
    const def = st.animDef;
    if (!key || def.frameWidth < 8 || def.frameHeight < 8) return;

    const tex = this.textures.get(key);
    if (!tex) return;

    const source = tex.getSourceImage() as HTMLImageElement;
    this.textures.remove(key);
    this.textures.addSpriteSheet(key, source, {
      frameWidth: def.frameWidth,
      frameHeight: def.frameHeight,
    });

    const newTex = this.textures.get(key);
    st.frameCount = newTex ? newTex.frameTotal - 1 : 0;

    const hadBbox = def.frames.some(f => f.bboxW != null && f.bboxW > 0);

    while (def.frames.length < st.frameCount) {
      def.frames.push({ duration: 100, offsetX: 0, offsetY: 0, scale: 1 });
    }
    def.frames.length = st.frameCount;
    for (const f of def.frames) {
      f.bboxX = undefined; f.bboxY = undefined;
      f.bboxW = undefined; f.bboxH = undefined;
      f.offsetX = 0; f.offsetY = 0; f.scale = 1;
    }
    def.frameOrder = Array.from({ length: st.frameCount }, (_, i) => i);

    this.autoSave();

    const fw = def.frameWidth;
    const fh = def.frameHeight;
    this.previewPanel.frameCountLabel.setText(`${st.frameCount} frames  •  ${fw}×${fh}px`);
    if (st.selectedFrame >= st.frameCount) {
      st.selectedFrame = st.frameCount - 1;
    }
    if (hadBbox && st.frameCount > 0) {
      this.sourcePanel.autoFitFrames();
      this.showFrame(st.selectedFrame >= 0 ? st.selectedFrame : 0);
      this.toolbar.refreshTimeline();
      this.toolbar.refreshFrameProps();
      return;
    }
    this.showFrame(st.selectedFrame >= 0 ? st.selectedFrame : 0);
    this.toolbar.rebuildSidebarList();
    this.toolbar.refreshTimeline();
    this.toolbar.refreshFrameProps();
    // Don't call refreshSheetProps() here — it destroys the +/- buttons mid-hold
  }

  // ═══════════════════════════════════════════════════════
  // ── Playback ──
  // ═══════════════════════════════════════════════════════

  private showFrame(idx: number): void {
    this.previewPanel.showFrame(idx);
  }

  private startPlayback(): void {
    const st = this.state;
    st.playing = true;
    st.playFrameIdx = st.selectedFrame >= 0 ? st.selectedFrame : 0;
    st.playTimer = 0;
    st.selectedFrame = -1;
    this.showFrame(st.playFrameIdx);
    this.toolbar.refreshTimeline();
    this.toolbar.refreshFrameProps();
  }

  private stopPlayback(): void {
    this.state.playing = false;
  }

  update(_time: number, delta: number): void {
    const st = this.state;
    if (!st.playing || st.frameCount === 0) return;

    st.playTimer += delta;
    const frameDef = st.animDef.frames[st.playFrameIdx];
    const dur = frameDef?.duration ?? 100;

    if (st.playTimer >= dur) {
      st.playTimer -= dur;
      st.playFrameIdx++;

      if (st.playFrameIdx >= st.frameCount) {
        if (st.animDef.repeat === -1) {
          st.playFrameIdx = 0;
        } else {
          st.playFrameIdx = st.frameCount - 1;
          this.stopPlayback();
          this.toolbar.refreshControls();
          return;
        }
      }

      this.showFrame(st.playFrameIdx);
    }
  }

  // ═══════════════════════════════════════════════════════
  // ── Persistence ──
  // ═══════════════════════════════════════════════════════

  private autoSave(): void {
    saveAnimDefs(this.state.allDefs);
  }

  /** Extract the relative path from the texture source URL */
  private getSourceFilename(key: string): string {
    // 1) Try the build-time manifest (most reliable — always a clean relative path)
    const manifestEntry = spritesheetManifest.find(e => e.key === key);
    if (manifestEntry) {
      // manifest paths start with "/" — strip leading slash for relative path
      return manifestEntry.path.replace(/^\//, '');
    }

    // 2) Fallback: extract from texture source URL
    const tex = this.textures.get(key);
    const src = (tex?.getSourceImage() as HTMLImageElement)?.src ?? '';
    if (src.startsWith('data:') || src.startsWith('blob:')) return `assets/${key}.png`;
    try {
      const url = new URL(src);
      let rel = url.pathname.replace(/^\//, '');
      if (!rel.toLowerCase().endsWith('.png')) rel += '.png';
      return rel || `assets/${key}.png`;
    } catch {
      const name = src.split('/').pop() ?? `${key}.png`;
      return name.toLowerCase().endsWith('.png') ? name : `${name}.png`;
    }
  }
}
